"use client"

import type React from "react"
import { useState } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { useAuth } from "@/context/auth-context"
import {
  AuthShell,
  SubmitBtn,
  AltLink,
  MailIcon,
  LockIcon,
  UserIcon,
  EyeIcon,
} from "@/components/auth-shell"

export default function RegisterPage() {
  const router = useRouter()
  const { register } = useAuth()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long")
      return
    }
    setLoading(true)
    try {
      const result = await register(email, password, name)
      if (result.success) {
        router.push("/login?registered=true")
      } else {
        setError(result.message || "Registration failed")
      }
    } catch {
      setError("An error occurred during registration")
    } finally {
      setLoading(false)
    }
  }

  const inputContainerStyle = {
    display: "flex",
    alignItems: "center",
    height: 46,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    overflow: "hidden",
  }

  const iconStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 46,
    color: "var(--muted)",
  }

  const inputStyle = {
    flex: 1,
    height: "100%",
    border: "none",
    background: "transparent",
    color: "var(--text)",
    fontSize: 14,
    outline: "none",
  }

  return (
    <AuthShell tag="useradd --create-home">
      <div style={{ textAlign: "center", marginBottom: 26 }}>
        <h1
          style={{
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: "-0.025em",
            color: "var(--text)",
            margin: 0,
          }}
        >
          Create an account
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-2)", marginTop: 8 }}>
          Get started with HSVE today
        </p>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 20,
            padding: "12px 14px",
            borderRadius: 6,
            border: "1px solid oklch(0.5 0.15 25 / 0.3)",
            background: "oklch(0.5 0.15 25 / 0.1)",
            fontSize: 13,
            color: "oklch(0.7 0.15 25)",
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Full Name field */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 500,
                color: "var(--text-2)",
                marginBottom: 8,
              }}
            >
              Full Name{" "}
              <span style={{ color: "var(--muted)", fontWeight: 400 }}>(optional)</span>
            </label>
            <div style={inputContainerStyle}>
              <span style={iconStyle}>
                <UserIcon />
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                style={{ ...inputStyle, paddingRight: 14 }}
              />
            </div>
          </div>

          {/* Email field */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 500,
                color: "var(--text-2)",
                marginBottom: 8,
              }}
            >
              Email
            </label>
            <div style={inputContainerStyle}>
              <span style={iconStyle}>
                <MailIcon />
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{ ...inputStyle, paddingRight: 14 }}
              />
            </div>
          </div>

          {/* Password field */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 500,
                color: "var(--text-2)",
                marginBottom: 8,
              }}
            >
              Password
            </label>
            <div style={inputContainerStyle}>
              <span style={iconStyle}>
                <LockIcon />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 46,
                  height: "100%",
                  border: "none",
                  background: "transparent",
                  color: "var(--muted)",
                  cursor: "pointer",
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
          </div>

          {/* Confirm Password field */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 500,
                color: "var(--text-2)",
                marginBottom: 8,
              }}
            >
              Confirm Password
            </label>
            <div style={inputContainerStyle}>
              <span style={iconStyle}>
                <LockIcon />
              </span>
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 46,
                  height: "100%",
                  border: "none",
                  background: "transparent",
                  color: "var(--muted)",
                  cursor: "pointer",
                }}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon off={showConfirmPassword} />
              </button>
            </div>
          </div>

          <SubmitBtn loading={loading} loadingText="Creating account...">
            Create account
          </SubmitBtn>
        </div>
      </form>

      <AltLink>
        Already have an account?{" "}
        <Link
          href="/login"
          style={{ color: "var(--accent)", fontWeight: 500, textDecoration: "none" }}
        >
          Sign in
        </Link>
      </AltLink>
    </AuthShell>
  )
}
